import React, { useState, useEffect } from 'react';
import {
    Grid,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    makeStyles,
    TextField,
    Paper,
    Divider, // Import Paper component
} from '@material-ui/core';
import { useDispatch } from "react-redux";
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from "react-i18next";
import { Autocomplete } from '@material-ui/lab';
import { getUserPreference } from "../../../../utilities/common";
import {
    createKeyAccount,
} from "../../../../actions/incidentManagementActions";
import FullscreenLoading from '../../../Common/FullscreenLoading';
import { showAlertSnackbar } from '../../../../reducers/uiSlice';

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        display: 'flex',
        justifyContent: 'space-between',
        scrollbarWidth: 'none', /* Firefox  */
        '-ms-overflow-style': 'none', /* Internet Explorer  */
        '&::-webkit-scrollbar': {
            width: 0,
            height: 0,
        },
    },
    dialogActions: {
        marginTop: theme.spacing(2),
        marginRight: theme.spacing(2),
        marginBottom: theme.spacing(1),
        justifyContent: 'flex-end',
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
    },
    // Style for the Paper component
    dialogPaper: {
        width: '600px', // Set the desired width
        minHeight: 'flex', // Set the desired height
    },
    grid: {
        display: 'flex',
        alignItems: 'left',
        justifyContent: 'left',
        flexDirection: 'row',
        marginTop: theme.spacing(2), // 调整上方间距
        marginBottom: theme.spacing(-1), // 调整下方间距
    },
    textField: {
        margin: "5px"
    },
    root: {
        flexGrow: 1,
    },
}));

function KeyAccountCreateDialog({ open, onClose, partnerList, handleSearch }
) {
    const dispatch = useDispatch();
    const classes = useStyles();
    const { t } = useTranslation('incidentmanagement');
    const userPref = getUserPreference();
    const [isDataLoading, setIsDataLoading] = useState(false);

    const [name, setName] = useState('');
    const [phone, setPhone] = useState(null);
    const [email, setEmail] = useState('');
    const [dingTalk, setDingTalk] = useState('');
    const [partners, setPartners] = useState([]);
    const [isCreateDisabled, setIsCreateDisabled] = useState(true); // 添加用于禁用按钮的状态

    const handleClose = () => {
        onClose();
    };

    const handleCreate = () => {
        setIsDataLoading(true);
        const params = {
            name: name.trim(),
            phone: phone || '',
            email: email.trim(),
            ding_talk_id: dingTalk || '',
            warehouse: userPref.userCity || '',
            partner_ids: partners || '',
        };

        if (params.name === '' || params.phone === 0 || params.email === '') {
            // 如果任何一个字段为空，不执行请求，显示错误消息
            dispatch(showAlertSnackbar({
                title: t('incident_management'),
                message: t('fields_required'),
                type: 'error'
            }));
            setIsDataLoading(false); // 停止加载状态
            return;
        }

        dispatch(createKeyAccount(params))
            .then((response) => {
                if (response.status === 'FAIL') {
                    dispatch(showAlertSnackbar({
                        title: t('incident_management'),
                        message: response.retMsg,
                        type: 'error'
                    }));
                } else {
                    dispatch(showAlertSnackbar({
                        title: t('incident_management'),
                        message: t('create_success'),
                        type: 'success'
                    }));
                    handleClose();
                    handleSearch();
                }
            })
            .catch((error) => {
                dispatch(showAlertSnackbar({
                    title: t('incident_management'),
                    message: t('create_failed'),
                    type: 'error'
                }));
            })
            .finally(() => {
                setIsDataLoading(false);
            });
    };

    useEffect(() => {
        setIsCreateDisabled(
            (typeof name !== 'string' || name.trim() === '') ||
            (typeof email !== 'string' || email.trim() === '') ||
            phone === 0
        );
    }, [name, phone, email]);

    return (
        <       Dialog open={open} onClose={handleClose} classes={{ paper: classes.dialogPaper }}>
            <DialogTitle>
                {t('create_key_account')}
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={handleClose}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <div className={classes.root}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Divider></Divider>
                        </Grid>
                        <Grid item xs={4} className={classes.grid}>
                            <TextField
                                id="name_textfield"
                                className={classes.textField}
                                label={t("common:name")}
                                name="phone"
                                variant="outlined"
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4} className={classes.grid}>
                            <TextField
                                id="phone_number_textfield"
                                className={classes.textField}
                                label={t("phone")}
                                name="phone"
                                variant="outlined"
                                onChange={(e) => setPhone(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4} className={classes.grid}>
                            <TextField
                                id="email_textfield"
                                className={classes.textField}
                                label={t("email")}
                                name="email"
                                variant="outlined"
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4} className={classes.grid}>
                            <TextField
                                id="dingtalk_textfield"
                                className={classes.textField}
                                label={t("ding_talk_id")}
                                name="ding_talk_id"
                                variant="outlined"
                                onChange={(e) => setDingTalk(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={8} className={classes.grid}>
                            <Autocomplete
                                className={classes.textField}
                                options={partnerList}
                                getOptionLabel={(option) => option?.name}
                                limitTags={4} // 设置显示的选项数量，根据需要调整
                                multiple // 启用多选
                                onChange={(event, value) => {
                                    setPartners(value.map((item) => item.id)); // 将所选项目的id添加到partners数组中
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label={t("partner")} variant="outlined" />
                                )}
                                id="advancedsearch_autocomplete"
                                value={partnerList.filter((option) => partners.includes(option.id))} // 根据partners数组来设置选择的值
                                fullWidth
                                margin="normal"
                            />

                        </Grid>
                    </Grid>
                </div>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button
                    id="incidentmanagement_keyaccount_create_button"
                    variant="contained"
                    color="primary"
                    className={classes.Button}
                    onClick={() => handleCreate()}
                    disabled={isCreateDisabled} // 设置禁用属性
                >
                    {t('create_new')}
                </Button>
            </DialogActions>
            <FullscreenLoading open={isDataLoading} />
        </Dialog>

    );
}

export default KeyAccountCreateDialog;
